import {decodeOrThrow} from '@raison/io-ts-tools';
import {isErroneousServerResult} from '@raison/network-facade';
import {debounce} from '@raison/toolbox';
import {MobilePhone, NonEmptyString, Password} from '@raison/types';
import {RootLayout, RsnButton, RsnDialog, RsnInput, RsnTextButton, useBoolean} from '@raison/ui-kit';
import * as Schema from 'effect/Schema';
import {isLeft} from 'fp-ts/Either';
import {absurd} from 'fp-ts/lib/function';
import React, {Fragment, useEffect, useState} from 'react';
import {Link, useLocation, useNavigate} from 'react-router';
import {CaptchaUsageMention} from '~/entities/captcha';
import {CommonLoaderDialog} from '~/entities/dialogs';
import {
    deActivateBiometry,
    get_biometry_type,
    get_touch_id_availability,
    gonative_auth,
    gonative_delete_secret,
    handleAuthDataReceived,
    isBiometryActivated,
    login,
} from '~/entities/user';
import {
    connectFieldWith,
    connectSubmitToImmerForm,
    defineFormStateValidation,
    fieldBlurRecipe,
    fieldsCompleteRecipe,
    FormState,
    Issues,
    useImmerState,
    userInputRecipe,
} from '~/library/form-models.v1';
import {useLocationState} from '~/library/navigation';
import {useUTM} from '~/processes/onboarding/lib/useUTM';
import {BiometryBrowserScreen} from '~/processes/onboarding/ui/shared/BiometryBrowserScreen';
import {BiometrySetupScreen} from '~/processes/onboarding/ui/shared/BiometrySetupScreen';
import {required, validatePhone} from '~/shared/legacy_presentational-components/Form/formValidate';
import TouchIdIcon from '~/shared/legacy_presentational-components/TouchIdIcon/TouchIdIcon';
import {appLocalStorage, appSessionStorage, clearManagedStorage} from '~/shared/persistence';
import {useAppDispatch} from '~/shared/store';
import {getBiometryHelper} from '~/shared/utils/biometryHelper';
import {ErrorHandlerReturnData, getErrorText} from '~/shared/utils/errorHandler';
import {clearFormatPhoneNumber, formatPhoneNumber} from '~/shared/utils/formatPhoneNumber';
import {hashPassword} from '~/shared/utils/hashPassword';
import {isAndroidOriOS, isMobileApp} from '~/shared/utils/mobileDeviceDetect';
import {capitalizeFirstLetter, mask5symbols} from '~/shared/utils/tools';
import {LoginErrorModal} from '../shared/LoginErrorModal';

const biometryHelper = getBiometryHelper();

type Values = {
    username: string;
    password: string;
};

type Step = 'username' | 'password' | 'setup-biometry' | 'biometry-browser';

type LoginFormState = ReturnType<typeof LoginFormState>;
const LoginFormState = (initial: Partial<Values> | null) =>
    FormState<Values>({
        username: initial?.username ?? '+',
        password: initial?.password ?? '',
    });

export function LoginPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const [dialog, setDialog] = useLocationState('login-page-dialog', Dialog);
    const closeDialog = () => navigate(-1);

    const savedUsername = appLocalStorage.username.get();
    const savedGivenName = appLocalStorage.givenName.get();
    const savedStatus = appLocalStorage.authStatus.get();

    const hasRegistered = savedUsername && !!savedStatus;

    const [step, setStep] = useState<Step>(hasRegistered ? 'password' : 'username');
    const [authError, setAuthError] = useState<ErrorHandlerReturnData | null>(null);
    const [biometryActivated, setBiometryActivated] = useState(isBiometryActivated);
    const [isLoading, loading] = useBoolean(false);
    const [isLoadingDialogOpen, loadingDialog] = useBoolean(false);
    const changeLoginLoaderState = (visible = false) => (visible ? loadingDialog.on() : loadingDialog.off());

    const utm = useUTM();

    const [form, setForm] = useImmerState(() =>
        LoginFormState({
            username: savedUsername ? formatPhoneNumber(savedUsername) : '+',
        })
    );

    const biometryAuth = () => {
        appLocalStorage.callPointBiometry.set('loginForm');

        const authErr = appLocalStorage.authError.get();

        if (!authErr) return gonative_auth();

        const err = JSON.parse(authErr);
        appLocalStorage.authError.remove();
        const error = getErrorText(err);

        if (error.title.includes('Usernames are not equal')) {
            setAuthError(error);

            deActivateBiometry();
            gonative_delete_secret();
        } else {
            setAuthError(error);
        }
        setDialog(Dialogs.loginError);
    };

    useEffect(() => {
        clearManagedStorage(appSessionStorage);

        if (appLocalStorage.runBiometryActivation.get() === 'start') {
            appLocalStorage.runBiometryActivation.remove();
            deActivateBiometry();
            gonative_delete_secret();
        }

        if (isBiometryActivated() && location.state?.disableBiometryAutorun !== true) setTimeout(biometryAuth, 700);

        get_touch_id_availability();

        biometryHelper.setLoaderLoginBiometry(changeLoginLoaderState);

        // the status may change while the app is in the background or biometry is deactivated on a lockout
        const disposeOnBiometryStatus = biometryHelper.setOnBiometryStatus(() =>
            setBiometryActivated(isBiometryActivated())
        );
        // a regular fail lets the user try again, only a lockout is worth a dialog
        const biometryName = get_biometry_type() === 'faceId' ? 'Face ID' : 'Touch ID';
        const disposeOnBiometryFailed = biometryHelper.setOnBiometryFailed(reason => {
            if (reason !== 'lockout') return;
            // the type is read on mount, during a lockout gonative may report it as 'none'
            setAuthError({
                title: `${biometryName} turned off`,
                context: `Too many failed attempts. Log in with your password. You can turn on ${biometryName} later in Settings after unlocking your phone with your passcode.`,
                type: 'alert',
            });
            setDialog(Dialogs.loginError);
        });

        return () => {
            delete window.oneSignalInfo;
            disposeOnBiometryStatus();
            disposeOnBiometryFailed();
        };
    }, []);

    const backBtnAction = () => {
        if (step === 'username') {
            navigate('/', {replace: true, state: {goToWelcomeScreen: true}});
        } else {
            changeStep('username');
        }
    };
    const canGoBack = !hasRegistered || step !== 'password';

    if (step === 'setup-biometry') {
        function restartApp() {
            window.restartAppAt('/');
        }

        return (
            <RootLayout navigateBack={false} title='Log in'>
                <BiometrySetupScreen
                    password={form.values.password}
                    onSuccess={restartApp}
                    onLater={restartApp}
                    callPoint={'loginForm'}
                />
            </RootLayout>
        );
    }

    if (step === 'biometry-browser') {
        return (
            <RootLayout navigateBack={false} title='Log in'>
                <BiometryBrowserScreen />
            </RootLayout>
        );
    }

    const changeStep = (nextStep: Step) => {
        if (nextStep === 'password') {
            setForm(form => {
                form.values.password = '';
                form.flags.password.completed = false;
            });
        }
        setStep(nextStep);
    };

    const validateLoginForm = defineFormStateValidation((values: Values) => {
        const issues: Issues<Values> = {};

        issues.username = validatePhone(clearFormatPhoneNumber(values.username));
        issues.password = required(values.password);

        return {values, issues};
    });

    const {values, ...computedForm} = validateLoginForm(form);

    const issues = {...computedForm.issues, ...computedForm.serverIssues};

    const signIn = async () => {
        const username = decodeOrThrow(MobilePhone, form.values.username);
        appLocalStorage.username.set(username);
        const password = decodeOrThrow(Password, form.values.password);

        const oneSignalId = window.oneSignalInfo?.oneSignalUserId;
        const oneSignalConsent = window.oneSignalInfo?.oneSignalSubscribed;

        const response = await login({
            username,
            password,
            onesignal_id: oneSignalId ? decodeOrThrow(NonEmptyString, oneSignalId) : undefined,
            onesignal_consent: oneSignalConsent,
        });

        if (isLeft(response)) {
            if (!isErroneousServerResult(response.left.error)) {
                throw response.left.error;
            }
            let error = response.left.error;
            if (error.message.includes('Wrong login or password')) {
                error = {...error, message: 'wrong login or password from login'};
            }
            const authError = getErrorText(error);
            setAuthError(authError);
            setDialog(Dialogs.loginError);
            return;
        }

        const result = response.right;
        const hPass = hashPassword(password, result.user.username);
        dispatch(handleAuthDataReceived(result));
        if (hPass) {
            appSessionStorage.hPass.set(hPass);
        }

        const hasTouchId = appLocalStorage.hasTouchId.get();
        const biometryStatus = appLocalStorage.biometryStatus.get();

        if (biometryStatus === 'Later' || isBiometryActivated()) {
            window.restartAppAt('/');
        } else if (hasTouchId) {
            changeStep('setup-biometry');
        } else if (isAndroidOriOS() && !isMobileApp()) {
            changeStep('biometry-browser');
        } else {
            window.restartAppAt('/');
        }
    };

    const loginByBiometry = () => {
        appLocalStorage.callPointBiometry.set('loginForm');
        gonative_auth();
    };

    const debouncedLoginByBiometry = debounce(loginByBiometry, 500);

    const connectSubmit = connectSubmitToImmerForm(setForm);
    const handleSubmit = connectSubmit(async () => {
        if (step === 'username') {
            if (issues.username) {
                setForm(fieldsCompleteRecipe('username'));
                return;
            }
            changeStep('password');
            return;
        }

        if (step === 'password') {
            if (issues.password) {
                setForm(fieldsCompleteRecipe('password'));
                return;
            }

            loading.on();
            loadingDialog.on();
            await signIn().finally(() => {
                loading.off();
                loadingDialog.off();
            });
            return;
        }
    });

    const connectField = connectFieldWith(computedForm, field => {
        setForm(fieldBlurRecipe(field));
    });

    const userInput = (update: Partial<Values>) => setForm(userInputRecipe(update));

    const changeAccount = () => {
        clearManagedStorage(appLocalStorage);
        clearManagedStorage(appSessionStorage);

        if (isMobileApp()) gonative_delete_secret();
        window.restartAppAt('/login');
    };

    const renderTitle = () => {
        switch (step) {
            case 'username': {
                return 'Phone number';
            }
            case 'password': {
                if (hasRegistered && savedGivenName) {
                    return `Hello, ${capitalizeFirstLetter(savedGivenName)}!`;
                }
                if (hasRegistered) {
                    return 'Hello!';
                }
                return 'Password';
            }
            default:
                absurd(step);
        }
    };

    const renderButtonText = () => {
        switch (step) {
            case 'username': {
                return 'Continue';
            }
            case 'password': {
                return 'Log in';
            }
            default:
                absurd(step);
        }
    };

    const handleForgotPassword = () => {
        appLocalStorage.username.set(form.values.username);
        navigate('/forgot-password');
    };

    return (
        <Fragment>
            <RootLayout navigateBack={canGoBack ? backBtnAction : false} title='Log in'>
                <form
                    onSubmit={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSubmit();
                    }}
                    className={'flex flex-1 flex-col'}
                >
                    <div className={'flex flex-1 flex-col gap-6 px-4 pb-4 pt-6'}>
                        <div className={'fv-text-l-regular flex flex-col gap-2'}>
                            <div className={'fv-heading-l text-neutral-100'}>{renderTitle()}</div>
                            <div className={'text-neutral-90'}>
                                {step === 'username' && (
                                    <Fragment>
                                        Please enter your phone number to log in <br />
                                        or{' '}
                                        <Link className={'fv-text-l-bold text-primary-30'} to={'/signup'}>
                                            sign up
                                        </Link>
                                        .
                                    </Fragment>
                                )}

                                {step === 'password' && (
                                    <Fragment>
                                        To log in to account{' '}
                                        {hasRegistered ? (
                                            <span className={'whitespace-nowrap'}>
                                                {mask5symbols(formatPhoneNumber(form.values.username))},
                                            </span>
                                        ) : (
                                            <strong className={'fv-text-l-bold whitespace-nowrap'}>
                                                {formatPhoneNumber(form.values.username)},
                                            </strong>
                                        )}
                                        <br />
                                        please enter your password.
                                    </Fragment>
                                )}
                            </div>
                            {step === 'password' && hasRegistered && (
                                <RsnTextButton.Inline onClick={changeAccount}>Change account</RsnTextButton.Inline>
                            )}
                        </div>
                        <div>
                            {step === 'username' && (
                                <RsnInput
                                    label={'Phone number'}
                                    type={'tel'}
                                    name={'username'}
                                    {...connectField('username')}
                                    value={values.username}
                                    onChange={e => {
                                        userInput({username: formatPhoneNumber(e.target.value)});
                                        appLocalStorage.username.set(clearFormatPhoneNumber(e.target.value));
                                    }}
                                />
                            )}

                            {step === 'password' && (
                                <RsnInput.Password
                                    label={'Password'}
                                    name={'password'}
                                    value={values.password}
                                    onChange={e => userInput({password: e.target.value})}
                                    {...connectField('password')}
                                />
                            )}

                            {step === 'password' && biometryActivated && (
                                <TouchIdIcon onClick={debouncedLoginByBiometry} />
                            )}

                            <input type='hidden' name='utm_source' value={utm.utm_source} />
                            <input type='hidden' name='utm_medium' value={utm.utm_medium} />
                            <input type='hidden' name='utm_campaign' value={utm.utm_campaign} />
                        </div>
                    </div>
                    <div className={'flex flex-col gap-2 p-4'}>
                        {step === 'username' && (
                            <Fragment>
                                <CaptchaUsageMention />
                            </Fragment>
                        )}
                        {step === 'password' && (
                            <RsnTextButton.Block onClick={handleForgotPassword}>Forgot password</RsnTextButton.Block>
                        )}
                        <RsnButton.Primary disabled={isLoading} type={'submit'}>
                            {renderButtonText()}
                        </RsnButton.Primary>
                    </div>
                </form>
            </RootLayout>

            <RsnDialog.Controller onClose={closeDialog}>
                {dialog === Dialogs.loginError && !!authError && (
                    <LoginErrorModal userName={form.values.username} {...authError} />
                )}
            </RsnDialog.Controller>
            <RsnDialog.Controller>
                {isLoadingDialogOpen && <CommonLoaderDialog reason={'Log in to your account...'} />}
            </RsnDialog.Controller>
        </Fragment>
    );
}

const Dialogs = {
    loginError: 'login-error',
} as const;
const Dialog = Schema.Literal(...Object.values(Dialogs));
